import { memo } from 'react'

import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { formatEventDuration } from './formatEventDuration'
import { fromDateTimeInput, toDateTimeInput } from './schedule'

export type ScheduleRangeFieldsProps = {
  /** ISO start time, or empty/cleared when not set. */
  startTime?: string | null
  /** ISO end time, or `null` for an open end. */
  endTime?: string | null
  onStartTimeChange: (value: string) => void
  onEndTimeChange: (value: string | null) => void
  /** Validation message for the start field (e.g. "a start time is required"). */
  startError?: string
  /** Validation message for the end field (e.g. "end before start"). */
  endError?: string
  disabled?: boolean
}

/**
 * The event schedule inputs — start, end, open end, and the resulting duration.
 * Presentational on purpose: both the create dialog and the config editor bind
 * it to their own form so the two flows stay identical to use.
 */
const ScheduleRangeFieldsRaw = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  startError,
  endError,
  disabled,
}: ScheduleRangeFieldsProps) => {
  const { t } = useTranslation(['domain'])

  const openEnd = endTime == null
  const duration = formatEventDuration(startTime, endTime)

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
      <TextField
        type="datetime-local"
        label={t('domain:Workspace.Event.startTime')}
        value={toDateTimeInput(startTime)}
        onChange={(e) => onStartTimeChange(fromDateTimeInput(e.target.value))}
        disabled={disabled}
        error={!!startError}
        helperText={startError}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        // While "open end" is on, drop the datetime-local input so its greyed
        // "dd.mm.yyyy, --:--" placeholder disappears — show an empty text field instead.
        type={openEnd ? 'text' : 'datetime-local'}
        label={t('domain:Workspace.Event.endTime')}
        value={openEnd ? '' : toDateTimeInput(endTime)}
        onChange={(e) => onEndTimeChange(fromDateTimeInput(e.target.value))}
        disabled={disabled || openEnd}
        error={!!endError}
        helperText={endError}
        InputLabelProps={{ shrink: true }}
      />
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Switch
            checked={openEnd}
            disabled={disabled}
            onChange={(e) => onEndTimeChange(e.target.checked ? null : (startTime ?? ''))}
          />
        }
        label={t('domain:Workspace.Event.openEnd')}
      />
      {/* Read-only: the length the picked start/end add up to. */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="heading-sm">{t('domain:Workspace.Event.duration')}</Typography>
        <Typography variant="text">{duration ?? '—'}</Typography>
      </Box>
    </Box>
  )
}

export const ScheduleRangeFields = memo(ScheduleRangeFieldsRaw)
